import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Copy, Check, Eye, EyeOff, Link2, Trash2, Plus, X, Pencil } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import ProfilePreview from '@/components/public-profile/ProfilePreview'
import SectionCard from '@/components/public-profile/SectionCard'
import FormInput from '@/components/public-profile/FormInput'
import ImageUpload from '@/components/public-profile/ImageUpload'
import athleteApi from '@/lib/api'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { PublicProfile, ProfileLink, CampaignSuggestion } from '@/types/public-profile'

interface EditableSocialChannel {
  platform: string
  handle: string
  verified?: boolean
}

const SOCIAL_PLATFORM_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'x', label: 'X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'threads', label: 'Threads' },
]

const sanitizeUsername = (value: string) => value.toLowerCase().replace(/[^a-z0-9_-]/g, '')

const getSuggestedUsername = (fullName?: string | null) => {
  const normalizedName = String(fullName || '')
    .trim()
    .toLowerCase()
  if (!normalizedName) return ''

  const nameParts = normalizedName
    .split(/\s+/)
    .map((part) => part.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))
    .map((part) => part.replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)

  if (nameParts.length === 0) return ''
  if (nameParts.length === 1) return nameParts[0]

  const firstInitial = nameParts[0].charAt(0)
  const lastName = nameParts[nameParts.length - 1]

  return `${firstInitial}${lastName}`
}

const PublicProfilePage = () => {
  const { athlete, isLoading: authLoading } = useAuth()
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [campaigns, setCampaigns] = useState<CampaignSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [classYearInput, setClassYearInput] = useState('')

  // Local state for links (to avoid API call on every keystroke)
  const [localLinks, setLocalLinks] = useState<{
    campaigns: ProfileLink[]
    links: ProfileLink[]
    partners: ProfileLink[]
  } | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [localSocialChannels, setLocalSocialChannels] = useState<EditableSocialChannel[]>([])
  const [hasUnsavedSocialChannels, setHasUnsavedSocialChannels] = useState(false)
  const [isSavingSocialChannels, setIsSavingSocialChannels] = useState(false)
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set())

  // Form state for new profile
  const [newUsername, setNewUsername] = useState('')
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [checkingUsername, setCheckingUsername] = useState(false)

  useEffect(() => {
    if (profile || newUsername) return

    const suggestion = sanitizeUsername(getSuggestedUsername(athlete?.name))
    if (!suggestion) return

    setNewUsername(suggestion)
  }, [athlete?.name, newUsername, profile])

  const buildSocialUrl = (platform: string, handle: string) => {
    const p = String(platform || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '')
    const rawHandle = String(handle || '').trim()
    if (/^https?:\/\//i.test(rawHandle)) return rawHandle
    const h = rawHandle.replace(/^@/, '')

    if (!p || !h) return null

    switch (p) {
      case 'instagram':
        return `https://www.instagram.com/${h}`
      case 'tiktok':
        return `https://www.tiktok.com/@${h}`
      case 'youtube':
        return `https://www.youtube.com/@${h}`
      case 'facebook':
        return `https://www.facebook.com/${h}`
      case 'snapchat':
        return `https://www.snapchat.com/add/${h}`
      case 'linkedin':
        return `https://www.linkedin.com/in/${h}`
      case 'x':
      case 'twitter':
        return `https://x.com/${h}`
      case 'twitch':
        return `https://www.twitch.tv/${h}`
      case 'pinterest':
        return `https://www.pinterest.com/${h}`
      case 'threads':
        return `https://www.threads.net/@${h}`
      default:
        return null
    }
  }

  // Fetch profile data
  useEffect(() => {
    const fetchData = async () => {
      // Wait for auth to complete
      if (authLoading) return

      // If not logged in, stop loading
      if (!athlete) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        // Fetch profile using athleteApi (axios with cookie auth)
        const profileRes = await athleteApi.get('/athlete/profile/public')
        if (profileRes.data.success && profileRes.data.data) {
          setProfile(profileRes.data.data)
        }

        const socialRes = await athleteApi.get('/athlete/profile/public/social-channels')
        if (socialRes.data.success && Array.isArray(socialRes.data.data)) {
          const channels = socialRes.data.data.map((channel: any) => ({
            platform: String(channel.platform || ''),
            handle: String(channel.handle || ''),
            verified: !!channel.verified,
          }))

          setLocalSocialChannels(channels)
          setHasUnsavedSocialChannels(false)
        }

        // Fetch campaign suggestions
        const suggestionsRes = await athleteApi.get(
          '/athlete/profile/public/suggestions/campaigns',
        )
        if (suggestionsRes.data.success) {
          setCampaigns(suggestionsRes.data.data)
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
        // Don't show error toast for 404 (no profile yet)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [athlete, authLoading])

  // Sync local links state when profile changes from API
  useEffect(() => {
    if (profile?.links) {
      setLocalLinks(profile.links)
      setHasUnsavedChanges(false)
    }
  }, [profile])

  // Keep class year input in sync with the latest profile value
  useEffect(() => {
    if (profile) {
      setClassYearInput(profile.classYear || '')
    }
  }, [profile?.classYear, profile])

  // Manual save function for links — accepts an optional override so remove handlers
  // can pass the freshly-computed state before React re-renders.
  const saveAllLinks = async (linksOverride?: typeof localLinks) => {
    const l = linksOverride ?? localLinks
    if (!l) return
    const allLinks = [...l.campaigns, ...l.links, ...l.partners]
    await handleLinksUpdate(allLinks)
  }

  // Check username availability with debounce
  useEffect(() => {
    if (!newUsername || newUsername.length < 3) {
      setUsernameAvailable(null)
      return
    }

    const timer = setTimeout(async () => {
      setCheckingUsername(true)
      try {
        const res = await athleteApi.get(`/athlete/profile/public/check-username/${newUsername}`)
        setUsernameAvailable(res.data.data?.isAvailable ?? false)
      } catch {
        setUsernameAvailable(null)
      } finally {
        setCheckingUsername(false)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [newUsername])

  const createProfile = async () => {
    if (!newUsername || !usernameAvailable) return

    setIsCreating(true)
    try {
      const res = await athleteApi.post('/athlete/profile/public', { username: newUsername })
      if (res.data.success) {
        setProfile(res.data.data)
        toast.success('Profile created!')
      } else {
        toast.error(res.data.error || 'Failed to create profile')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create profile')
    } finally {
      setIsCreating(false)
    }
  }

  const togglePublish = async () => {
    if (!profile) return

    setIsSaving(true)
    try {
      const endpoint = profile.isPublished ? 'unpublish' : 'publish'
      const res = await athleteApi.post(`/athlete/profile/public/${endpoint}`)
      if (res.data.success) {
        setProfile(res.data.data)
        toast.success(profile.isPublished ? 'Profile unpublished' : 'Profile published!')
      } else {
        toast.error(res.data.error || 'Failed to update profile')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  const copyProfileUrl = () => {
    if (profile?.publicUrl) {
      const fullUrl = `${window.location.origin}${profile.publicUrl}`
      navigator.clipboard.writeText(fullUrl)
      setCopied(true)
      toast.success('Link copied!')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleLinksUpdate = async (links: ProfileLink[]) => {
    setIsSaving(true)
    try {
      const res = await athleteApi.put('/athlete/profile/public/links', { links })
      if (res.data.success) {
        setProfile(res.data.data)
        toast.success('Links updated!')
      } else {
        toast.error(res.data.error || 'Failed to update links')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update links')
    } finally {
      setIsSaving(false)
    }
  }

  const handleProfileUpdate = async (
    updates: Partial<PublicProfile>,
    options: { showSuccessToast?: boolean } = {},
  ) => {
    const { showSuccessToast = true } = options
    setIsSaving(true)
    try {
      const res = await athleteApi.patch('/athlete/profile/public', updates)
      if (res.data.success) {
        setProfile(res.data.data)
        if (showSuccessToast) {
          toast.success('Profile updated!')
        }
      } else {
        toast.error(res.data.error || 'Failed to update profile')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  // Debounced class year autosave to avoid API calls/toasts on every keystroke
  useEffect(() => {
    if (!profile) return

    const currentClassYear = profile.classYear || ''
    if (classYearInput === currentClassYear) return

    const timer = setTimeout(() => {
      handleProfileUpdate({ classYear: classYearInput }, { showSuccessToast: false })
    }, 500)

    return () => clearTimeout(timer)
  }, [classYearInput, profile])

  const saveSocialChannels = async () => {
    setIsSavingSocialChannels(true)
    try {
      const channels = localSocialChannels
        .map((channel) => ({
          platform: channel.platform.trim(),
          handle: channel.handle.trim(),
        }))
        .filter((channel) => channel.platform && channel.handle)

      const res = await athleteApi.put('/athlete/profile/public/social-channels', { channels })

      if (!res.data.success) {
        toast.error(res.data.error || 'Failed to update social channels')
        return
      }

      const savedChannels = (res.data.data || []).map((channel: any) => ({
        platform: String(channel.platform || ''),
        handle: String(channel.handle || ''),
        verified: !!channel.verified,
      }))

      setLocalSocialChannels(savedChannels)
      setHasUnsavedSocialChannels(false)
      toast.success('Social channels updated!')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update social channels')
    } finally {
      setIsSavingSocialChannels(false)
    }
  }

  const startEditing = (id: string) => setEditingIds((prev) => new Set([...prev, id]))
  const stopEditing = (id: string) =>
    setEditingIds((prev) => {
      const s = new Set(prev)
      s.delete(id)
      return s
    })

  // Show loading while auth is checking or profile is loading
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  // No profile yet - show creation form
  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Create Your Public Profile</h1>
          <p className="text-gray-400 mt-1">Create a links page to share with your followers</p>
        </div>

        <Card className="bg-white/10 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Link2 className="h-5 w-5 text-[#E879F9]" />
              Choose Your Username
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-gray-300">
                Choose your username
              </Label>
              <div className="flex mt-2 rounded-md overflow-hidden border border-white/20 focus-within:border-white/40 transition-colors">
                <span className="px-3 flex items-center bg-white/5 text-gray-400 text-sm shrink-0 border-r border-white/20 select-none">
                  /p/
                </span>
                <Input
                  id="username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(sanitizeUsername(e.target.value))}
                  placeholder="username"
                  className="flex-1 bg-white/10 border-0 rounded-none text-white focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              {newUsername.length > 0 && (
                <p className="text-xs text-gray-500 mt-1.5 truncate">
                  {window.location.origin}/p/{newUsername}
                </p>
              )}
              {checkingUsername && (
                <p className="text-sm text-gray-400 mt-1">Checking availability...</p>
              )}
              {!checkingUsername && usernameAvailable === true && newUsername.length >= 3 && (
                <p className="text-sm text-green-400 mt-1">✓ Username is available</p>
              )}
              {!checkingUsername && usernameAvailable === false && (
                <p className="text-sm text-red-400 mt-1">✗ Username is taken</p>
              )}
            </div>

            <Button
              onClick={createProfile}
              disabled={!usernameAvailable || isCreating || newUsername.length < 3}
              variant="lime"
              className="w-full"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Profile'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Profile exists - show editor with preview
  const readOnlyFullName = athlete?.name || profile.name || ''
  const selectedSocialPlatforms = localSocialChannels
    .map((channel) => channel.platform)
    .filter(Boolean)

  return (
    <div className="w-full pb-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Public Profile</h1>
          <p className="text-gray-400 mt-1">Manage your public-facing profile page</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          {profile.publicUrl && (
            <Button
              variant="outline"
              onClick={copyProfileUrl}
              className="border-white/20 text-white hover:bg-white/10"
            >
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy Link
            </Button>
          )}
          <Button
            onClick={togglePublish}
            disabled={isSaving}
            variant={profile.isPublished ? 'outline' : 'default'}
            className={
              profile.isPublished
                ? 'border-white/20 text-white hover:bg-white/10'
                : 'bg-[#C0FD72] text-black hover:bg-[#B0ED62]'
            }
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : profile.isPublished ? (
              <EyeOff className="h-4 w-4 mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            {profile.isPublished ? 'Unpublish' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Status bar */}
      <div
        className={`mb-6 p-3 rounded-lg flex items-center justify-between ${
          profile.isPublished
            ? 'bg-green-500/10 border border-green-500/20'
            : 'bg-yellow-500/10 border border-yellow-500/20'
        }`}
      >
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${profile.isPublished ? 'bg-green-500' : 'bg-yellow-500'}`}
          />
          <span className={profile.isPublished ? 'text-green-400' : 'text-yellow-400'}>
            {profile.isPublished ? 'Published' : 'Draft'}
          </span>
        </div>
        {profile.publicUrl && (
          <div className="flex items-center gap-2 min-w-0">
            <div className="hidden sm:flex items-center bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 min-w-0">
              <span className="text-xs text-gray-400 font-mono truncate max-w-[220px] lg:max-w-[320px] select-all">
                {window.location.origin}
                {profile.publicUrl}
              </span>
            </div>
            <button
              onClick={copyProfileUrl}
              className="flex items-center gap-1.5 text-xs border border-white/10 rounded-md px-2.5 py-1.5 transition-colors whitespace-nowrap hover:bg-white/10"
              style={{ color: copied ? '#4ade80' : '#9ca3af' }}
              title={`Copy your profile link: ${window.location.origin}${profile.publicUrl}`}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied!' : 'Copy link to share'}
            </button>
          </div>
        )}
      </div>

      {/* Main content - Single Column Editor */}
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Basic Info */}
        <SectionCard title="Basic Information" hideDelete hideEditTitle>
          <FormInput
            label="Full Name"
            id="name"
            value={readOnlyFullName}
            readOnly
            disabled
            className="bg-gray-100 text-gray-500 cursor-not-allowed"
          />
          <FormInput
            label="Username (for URL)"
            id="username"
            value={profile.username}
            disabled={true}
            className="bg-gray-100 text-gray-500 cursor-not-allowed"
          />
          <FormInput
            label="Class Year"
            id="classYear"
            value={classYearInput}
            onChange={(e) => setClassYearInput(e.target.value)}
            placeholder="Class of 2026"
          />
          {profile.heroImageUrl && profile.heroImageUrl !== athlete?.avatar ? (
            <ImageUpload
              label="Hero Image"
              value={profile.heroImageUrl}
              onChange={(value) => handleProfileUpdate({ heroImageUrl: value })}
              usageType="hero"
            />
          ) : (
            <div className="space-y-3">
              {athlete?.avatar && (
                <div className="space-y-2">
                  <label
                    className="block text-sm text-gray-300 font-medium"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                  >
                    Hero Image
                  </label>
                  <div className="relative rounded-xl overflow-hidden bg-gray-800 border border-white/10 aspect-square">
                    <img
                      src={athlete.avatar}
                      alt="Profile photo used as hero"
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-black/60 px-3 py-2 text-xs text-gray-300">
                      Currently showing your profile photo
                    </div>
                  </div>
                </div>
              )}
              <ImageUpload
                label={athlete?.avatar ? 'Upload a custom hero image to override' : 'Hero Image'}
                value=""
                onChange={(value) => handleProfileUpdate({ heroImageUrl: value })}
                usageType="hero"
              />
            </div>
          )}
        </SectionCard>

        {/* Social Channels */}
        <SectionCard title="Social Channels" hideDelete hideEditTitle>
          <div className="space-y-4">
            {localSocialChannels.map((channel, index) => (
              <div
                key={`${channel.platform || 'social'}-${index}`}
                className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-3 relative group"
              >
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setLocalSocialChannels((prev) => prev.filter((_, i) => i !== index))
                      setHasUnsavedSocialChannels(true)
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`social-platform-select-${index}`} className="text-gray-700">
                    Platform
                  </Label>
                  <Select
                    value={channel.platform}
                    onValueChange={(value) => {
                      setLocalSocialChannels((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, platform: value, verified: false } : item,
                        ),
                      )
                      setHasUnsavedSocialChannels(true)
                    }}
                  >
                    <SelectTrigger
                      id={`social-platform-select-${index}`}
                      className="w-full bg-white border-gray-200 text-[#11181C]"
                    >
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 text-[#11181C]">
                      {SOCIAL_PLATFORM_OPTIONS.map((platform) => (
                        <SelectItem
                          key={platform.value}
                          value={platform.value}
                          className="data-highlighted:bg-brand-lime data-highlighted:text-black data-[state=checked]:bg-brand-lime/70 data-[state=checked]:text-black"
                          disabled={
                            selectedSocialPlatforms.includes(platform.value) &&
                            channel.platform !== platform.value
                          }
                        >
                          {platform.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <FormInput
                  label="Handle or URL"
                  id={`social-handle-${index}`}
                  value={channel.handle}
                  onChange={(e) => {
                    const value = e.target.value
                    setLocalSocialChannels((prev) =>
                      prev.map((item, i) => (i === index ? { ...item, handle: value } : item)),
                    )
                    setHasUnsavedSocialChannels(true)
                  }}
                  placeholder="@yourhandle"
                />

                {channel.verified && <p className="text-xs text-[#5DBED9]">Verified connection</p>}
              </div>
            ))}

            <Button
              className="w-full bg-[#11181C] text-[#C0FD72] border border-[#C0FD72]/20 hover:bg-[#C0FD72] hover:text-[#0A1A1F] h-12 transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={() => {
                setLocalSocialChannels((prev) => [
                  ...prev,
                  { platform: '', handle: '', verified: false },
                ])
                setHasUnsavedSocialChannels(true)
              }}
            >
              + Add Social Channel
            </Button>
            {hasUnsavedSocialChannels && (
              <Button
                onClick={saveSocialChannels}
                disabled={isSavingSocialChannels}
                variant="lime"
                className="w-full"
              >
                {isSavingSocialChannels ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Channels
              </Button>
            )}
          </div>
        </SectionCard>

        {/* Campaigns */}
        <SectionCard title="Current Campaigns" hideDelete hideEditTitle>
          <p className="text-sm text-gray-500 pb-1">
            Showcase brands you're actively working with. Add your tracking links, affiliate URLs,
            or any campaign link so your followers can shop, sign up, or engage directly from your
            profile.
          </p>
          <div className="space-y-4">
            {(localLinks?.campaigns || profile.links.campaigns).map((campaign, index) => {
              const removeCampaign = () => {
                const newCampaigns = (localLinks?.campaigns || profile.links.campaigns).filter(
                  (_, i) => i !== index,
                )
                const links = localLinks?.links || profile.links.links
                const partners = localLinks?.partners || profile.links.partners
                const newLinks = { campaigns: newCampaigns, links, partners }
                setLocalLinks(newLinks)
                stopEditing(campaign.id)
                saveAllLinks(newLinks)
              }

              if (!editingIds.has(campaign.id)) {
                return (
                  <div
                    key={campaign.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    {campaign.brandLogo ? (
                      <img
                        src={campaign.brandLogo}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <Link2 className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {campaign.label || 'Untitled'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {campaign.url || 'No URL set'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEditing(campaign.id)}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={removeCampaign}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )
              }

              return (
                <div
                  key={campaign.id}
                  className="p-4 rounded-xl border border-[#C0FD72]/50 bg-[#C0FD72]/5 space-y-3"
                >
                  <FormInput
                    label="Label"
                    id={`campaign-label-${index}`}
                    value={campaign.label}
                    onChange={(e) => {
                      const newCampaigns = [...(localLinks?.campaigns || profile.links.campaigns)]
                      newCampaigns[index] = { ...newCampaigns[index], label: e.target.value }
                      const links = localLinks?.links || profile.links.links
                      const partners = localLinks?.partners || profile.links.partners
                      setLocalLinks({ campaigns: newCampaigns, links, partners })
                      setHasUnsavedChanges(true)
                    }}
                    placeholder="Campaign Name"
                  />
                  <FormInput
                    label="URL"
                    id={`campaign-url-${index}`}
                    value={campaign.url}
                    onChange={(e) => {
                      const newCampaigns = [...(localLinks?.campaigns || profile.links.campaigns)]
                      newCampaigns[index] = { ...newCampaigns[index], url: e.target.value }
                      const links = localLinks?.links || profile.links.links
                      const partners = localLinks?.partners || profile.links.partners
                      setLocalLinks({ campaigns: newCampaigns, links, partners })
                      setHasUnsavedChanges(true)
                    }}
                    placeholder="https://..."
                  />
                  <ImageUpload
                    label="Button Image"
                    value={campaign.brandLogo || ''}
                    onChange={(value) => {
                      const newCampaigns = [...(localLinks?.campaigns || profile.links.campaigns)]
                      newCampaigns[index] = { ...newCampaigns[index], brandLogo: value }
                      const links = localLinks?.links || profile.links.links
                      const partners = localLinks?.partners || profile.links.partners
                      setLocalLinks({ campaigns: newCampaigns, links, partners })
                      setHasUnsavedChanges(true)
                    }}
                    shape="round"
                    usageType="brand"
                  />
                  <div className="flex items-center justify-between pt-2 border-t border-[#C0FD72]/30">
                    <button
                      onClick={removeCampaign}
                      className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1.5 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                    <Button
                      onClick={() => {
                        stopEditing(campaign.id)
                        saveAllLinks()
                      }}
                      size="sm"
                      disabled={isSaving}
                      variant="lime"
                    >
                      {isSaving ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Done
                    </Button>
                  </div>
                </div>
              )
            })}
            {/* Quick Add Suggestions */}
            {campaigns.filter(
              (c) => !profile.links.campaigns.some((l) => l.collaborationId === c.id),
            ).length > 0 && (
              <div className="pt-1 pb-1">
                <Label
                  className="text-xs text-gray-400 mb-2.5 block uppercase font-medium tracking-wide"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Add from your collaborations:
                </Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {campaigns
                    .filter(
                      (c) => !profile.links.campaigns.some((l) => l.collaborationId === c.id),
                    )
                    .slice(0, 5)
                    .map((suggestion) => (
                      <Button
                        key={suggestion.id}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newCampaign = {
                            id: crypto.randomUUID(),
                            sectionType: 'campaign',
                            label: suggestion.label,
                            url: suggestion.url || '',
                            brandLogo: suggestion.brandLogo,
                            displayOrder: profile.links.campaigns.length,
                            isEnabled: true,
                            sectionTitle: null,
                            collaborationId: suggestion.id,
                          }
                          const campaigns = localLinks?.campaigns || profile.links.campaigns
                          const links = localLinks?.links || profile.links.links
                          const partners = localLinks?.partners || profile.links.partners
                          const newLinks = {
                            campaigns: [...campaigns, newCampaign],
                            links,
                            partners,
                          }
                          setLocalLinks(newLinks)
                          saveAllLinks(newLinks)
                        }}
                        className="text-xs border bg-white rounded-lg border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50 shadow-xs"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        <Plus className="h-3 w-3 mr-1.5" />
                        {suggestion.label}
                      </Button>
                    ))}
                </div>
              </div>
            )}

            <Button
              className="w-full bg-[#11181C] text-[#C0FD72] border border-[#C0FD72]/20 hover:bg-[#C0FD72] hover:text-[#0A1A1F] h-12 transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={() => {
                const newId = crypto.randomUUID()
                const newCampaign = {
                  id: newId,
                  sectionType: 'campaign',
                  label: '',
                  url: '',
                  brandLogo: '',
                  displayOrder: (localLinks?.campaigns || profile.links.campaigns).length,
                  isEnabled: true,
                  sectionTitle: null,
                }
                const campaigns = localLinks?.campaigns || profile.links.campaigns
                const links = localLinks?.links || profile.links.links
                const partners = localLinks?.partners || profile.links.partners
                setLocalLinks({ campaigns: [...campaigns, newCampaign], links, partners })
                setHasUnsavedChanges(true)
                startEditing(newId)
              }}
            >
              + Add Custom Campaign
            </Button>
          </div>
        </SectionCard>

        {/* Links */}
        <SectionCard title="My Links" hideDelete hideEditTitle>
          <div className="space-y-4">
            {(localLinks?.links || profile.links.links).map((link, index) => {
              const removeLink = () => {
                const newLinksSection = (localLinks?.links || profile.links.links).filter(
                  (_, i) => i !== index,
                )
                const campaigns = localLinks?.campaigns || profile.links.campaigns
                const partners = localLinks?.partners || profile.links.partners
                const newLinks = { campaigns, links: newLinksSection, partners }
                setLocalLinks(newLinks)
                stopEditing(link.id)
                saveAllLinks(newLinks)
              }

              if (!editingIds.has(link.id)) {
                return (
                  <div
                    key={link.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    {link.brandLogo ? (
                      <img
                        src={link.brandLogo}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <Link2 className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {link.label || 'Untitled'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{link.url || 'No URL set'}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEditing(link.id)}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={removeLink}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )
              }

              return (
                <div
                  key={link.id}
                  className="p-4 rounded-xl border border-[#C0FD72]/50 bg-[#C0FD72]/5 space-y-3"
                >
                  <FormInput
                    label="Label"
                    id={`link-label-${index}`}
                    value={link.label}
                    onChange={(e) => {
                      const newLinksSection = [...(localLinks?.links || profile.links.links)]
                      newLinksSection[index] = { ...newLinksSection[index], label: e.target.value }
                      const campaigns = localLinks?.campaigns || profile.links.campaigns
                      const partners = localLinks?.partners || profile.links.partners
                      setLocalLinks({ campaigns, links: newLinksSection, partners })
                      setHasUnsavedChanges(true)
                    }}
                    placeholder="Link Label"
                  />
                  <FormInput
                    label="URL"
                    id={`link-url-${index}`}
                    value={link.url}
                    onChange={(e) => {
                      const newLinksSection = [...(localLinks?.links || profile.links.links)]
                      newLinksSection[index] = { ...newLinksSection[index], url: e.target.value }
                      const campaigns = localLinks?.campaigns || profile.links.campaigns
                      const partners = localLinks?.partners || profile.links.partners
                      setLocalLinks({ campaigns, links: newLinksSection, partners })
                      setHasUnsavedChanges(true)
                    }}
                    placeholder="https://..."
                  />
                  <ImageUpload
                    label="Button Image"
                    value={link.brandLogo || ''}
                    onChange={(value) => {
                      const newLinksSection = [...(localLinks?.links || profile.links.links)]
                      newLinksSection[index] = { ...newLinksSection[index], brandLogo: value }
                      const campaigns = localLinks?.campaigns || profile.links.campaigns
                      const partners = localLinks?.partners || profile.links.partners
                      setLocalLinks({ campaigns, links: newLinksSection, partners })
                      setHasUnsavedChanges(true)
                    }}
                    shape="round"
                    usageType="brand"
                  />
                  <div className="flex items-center justify-between pt-2 border-t border-[#C0FD72]/30">
                    <button
                      onClick={removeLink}
                      className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1.5 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                    <Button
                      onClick={() => {
                        stopEditing(link.id)
                        saveAllLinks()
                      }}
                      size="sm"
                      disabled={isSaving}
                      variant="lime"
                    >
                      {isSaving ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Done
                    </Button>
                  </div>
                </div>
              )
            })}
            <Button
              className="w-full bg-[#11181C] text-[#C0FD72] border border-[#C0FD72]/20 hover:bg-[#C0FD72] hover:text-[#0A1A1F] h-12 transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={() => {
                const newId = crypto.randomUUID()
                const newLink = {
                  id: newId,
                  sectionType: 'link',
                  label: '',
                  url: '',
                  brandLogo: '',
                  displayOrder: (localLinks?.links || profile.links.links).length,
                  isEnabled: true,
                  sectionTitle: null,
                }
                const campaigns = localLinks?.campaigns || profile.links.campaigns
                const links = localLinks?.links || profile.links.links
                const partners = localLinks?.partners || profile.links.partners
                setLocalLinks({ campaigns, links: [...links, newLink], partners })
                setHasUnsavedChanges(true)
                startEditing(newId)
              }}
            >
              + Add Link
            </Button>
          </div>
        </SectionCard>

        {/* Partners */}
        <SectionCard title="Official Brand Partners" hideDelete hideEditTitle>
          <div className="space-y-4">
            {(localLinks?.partners || profile.links.partners).map((partner, index) => {
              const removePartner = () => {
                const newPartners = (localLinks?.partners || profile.links.partners).filter(
                  (_, i) => i !== index,
                )
                const campaigns = localLinks?.campaigns || profile.links.campaigns
                const links = localLinks?.links || profile.links.links
                const newLinks = { campaigns, links, partners: newPartners }
                setLocalLinks(newLinks)
                stopEditing(partner.id)
                saveAllLinks(newLinks)
              }

              if (!editingIds.has(partner.id)) {
                return (
                  <div
                    key={partner.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    {partner.brandLogo ? (
                      <img
                        src={partner.brandLogo}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <Link2 className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {partner.label || 'Untitled'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {partner.url || 'No URL set'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEditing(partner.id)}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={removePartner}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )
              }

              return (
                <div
                  key={partner.id}
                  className="p-4 rounded-xl border border-[#C0FD72]/50 bg-[#C0FD72]/5 space-y-3"
                >
                  <FormInput
                    label="Brand Name"
                    id={`partner-name-${index}`}
                    value={partner.label}
                    onChange={(e) => {
                      const newPartners = [...(localLinks?.partners || profile.links.partners)]
                      newPartners[index] = { ...newPartners[index], label: e.target.value }
                      const campaigns = localLinks?.campaigns || profile.links.campaigns
                      const links = localLinks?.links || profile.links.links
                      setLocalLinks({ campaigns, links, partners: newPartners })
                      setHasUnsavedChanges(true)
                    }}
                    placeholder="Brand Name"
                  />
                  <FormInput
                    label="URL"
                    id={`partner-url-${index}`}
                    value={partner.url}
                    onChange={(e) => {
                      const newPartners = [...(localLinks?.partners || profile.links.partners)]
                      newPartners[index] = { ...newPartners[index], url: e.target.value }
                      const campaigns = localLinks?.campaigns || profile.links.campaigns
                      const links = localLinks?.links || profile.links.links
                      setLocalLinks({ campaigns, links, partners: newPartners })
                      setHasUnsavedChanges(true)
                    }}
                    placeholder="https://..."
                  />
                  <ImageUpload
                    label="Button Image"
                    value={partner.brandLogo || ''}
                    onChange={(value) => {
                      const newPartners = [...(localLinks?.partners || profile.links.partners)]
                      newPartners[index] = { ...newPartners[index], brandLogo: value }
                      const campaigns = localLinks?.campaigns || profile.links.campaigns
                      const links = localLinks?.links || profile.links.links
                      setLocalLinks({ campaigns, links, partners: newPartners })
                      setHasUnsavedChanges(true)
                    }}
                    shape="round"
                    usageType="partner"
                  />
                  <div className="flex items-center justify-between pt-2 border-t border-[#C0FD72]/30">
                    <button
                      onClick={removePartner}
                      className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1.5 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                    <Button
                      onClick={() => {
                        stopEditing(partner.id)
                        saveAllLinks()
                      }}
                      size="sm"
                      disabled={isSaving}
                      variant="lime"
                    >
                      {isSaving ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Done
                    </Button>
                  </div>
                </div>
              )
            })}
            <Button
              className="w-full bg-[#11181C] text-[#C0FD72] border border-[#C0FD72]/20 hover:bg-[#C0FD72] hover:text-[#0A1A1F] h-12 transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              onClick={() => {
                const newId = crypto.randomUUID()
                const newPartner = {
                  id: newId,
                  sectionType: 'partner',
                  label: '',
                  url: '',
                  brandLogo: '',
                  displayOrder: (localLinks?.partners || profile.links.partners).length,
                  isEnabled: true,
                  sectionTitle: null,
                }
                const campaigns = localLinks?.campaigns || profile.links.campaigns
                const links = localLinks?.links || profile.links.links
                const partners = localLinks?.partners || profile.links.partners
                setLocalLinks({ campaigns, links, partners: [...partners, newPartner] })
                setHasUnsavedChanges(true)
                startEditing(newId)
              }}
            >
              + Add Partner
            </Button>
          </div>
        </SectionCard>
      </div>

      {/* Full Screen Preview Overlay */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black overflow-y-auto animate-in fade-in duration-200">
          <Button
            className="fixed top-6 right-6 z-50 rounded-full bg-black/50 hover:bg-black/70 text-white border border-white/10 w-10 h-10 p-0"
            onClick={() => setShowPreview(false)}
          >
            <X className="h-5 w-5" />
          </Button>
          <ProfilePreview
            profile={{
              ...profile,
              socialChannels: localSocialChannels.map((channel) => ({
                platform: channel.platform,
                handle: channel.handle,
                url: buildSocialUrl(channel.platform, channel.handle),
                verified: !!channel.verified,
              })),
            }}
            athlete={athlete}
            isFullPage={true}
          />
        </div>
      )}
    </div>
  )
}

export default PublicProfilePage
